export interface Money {
  currency: string;
  amount: number;
}

export interface ExchangeRatesResponse {
  base: string;
  date: string;
  rates: Record<string, number>;
  timestamp: string;
  source?: string;
  isStale?: boolean;
}

export interface ConvertedMoney {
  original: Money;
  converted: Money;
  rate: number;
  rateTimestamp: string;
  isStale: boolean;
}

export interface ConvertedDisplay {
  primary: string;
  secondary: string | null;
  tooltip: ConversionTooltip;
}

export interface ConversionTooltip {
  originalAmount: string;
  convertedAmount: string;
  rate: string;
  rateDate: string;
  isStale: boolean;
}

export interface CurrencyPreferences {
  displayCurrency: string;
  showOriginalCurrency: boolean;
}

export interface CurrencyContextValue {
  displayCurrency: string;
  baseCurrency: string;
  exchangeRates: Record<string, number>;
  isLoading: boolean;
  isStale: boolean;
  lastUpdated: string | null;
  error: string | null;
  preferences: CurrencyPreferences;
  setDisplayCurrency: (currency: string) => void;
  setShowOriginalCurrency: (show: boolean) => void;
  convert: (
    money: Money | null | undefined,
    targetCurrency?: string,
  ) => ConvertedMoney | null;
  formatMoney: (money: Money | null | undefined) => string;
  formatConverted: (money: Money | null | undefined) => string;
  formatWithOriginal: (
    money: Money | null | undefined,
  ) => ConvertedDisplay | null;
  getRate: (fromCurrency: string, toCurrency: string) => number | null;
  refreshRates: () => Promise<void>;
}

export interface CurrencyProviderProps {
  children: React.ReactNode;
  defaultCurrency?: string;
  showOriginal?: boolean;
  onCurrencyChange?: (currency: string) => void;
  fetchRates?: () => Promise<ExchangeRatesResponse>;
  enabled?: boolean;
}

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
  decimals?: number;
}

export const SUPPORTED_CURRENCIES: CurrencyInfo[] = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "\u20AC", name: "Euro" },
  { code: "GBP", symbol: "\u00A3", name: "British Pound" },
  { code: "GHS", symbol: "GH\u20B5", name: "Ghana Cedi" },
  { code: "NGN", symbol: "\u20A6", name: "Nigerian Naira" },
  { code: "KES", symbol: "KSh", name: "Kenyan Shilling" },
  { code: "ZAR", symbol: "R", name: "South African Rand" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "JPY", symbol: "\u00A5", name: "Japanese Yen", decimals: 0 },
  { code: "CNY", symbol: "\u00A5", name: "Chinese Yuan" },
  { code: "INR", symbol: "\u20B9", name: "Indian Rupee" },
  { code: "CHF", symbol: "CHF", name: "Swiss Franc" },
];

export const CURRENCY_SYMBOLS: Record<string, string> = Object.fromEntries(
  SUPPORTED_CURRENCIES.map((c) => [c.code, c.symbol]),
);
